import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Grid, Modal } from "@mui/material";
import { Table } from "antd";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import LoadingBackdrop from "../components/general/LoadingBackdrop";
import NunitoText from "../components/general/NunitoText";
import ReusableButton from "../components/general/ReusableButton";
import ReusableSelect from "../components/general/ReusableSelect";
import ReusableTextField from "../components/general/ReusableTextField";
import AddNewCompetitionForCertificateModal from "../components/modal/AddNewCompetitionForCertificateModal";
import ConfirmDeleteModal from "../components/modal/ConfirmDeleteModal";
import { isAdmin } from "../functions/checkrole";
import { deleteCertificateConfiguration } from "../functions/deleteData";
import { cancelAction } from "../functions/general";
import {
  getConfiguredNewCompetitionsInPrintCertificates,
  getConfigureNewCompetitionInPrintCertificatesFilter,
} from "../functions/getData";
import {
  addNewCompetitionInPrintCertificates,
  updateCertificateConfiguration,
} from "../functions/postData";
import { showNotification } from "../functions/snackbar";

const ConfigureCertificate = () => {
  document.title = "Configure Certificate";
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [onAddCompetition, setOnAddCompetition] = useState(false);
  const [onEditCompetition, setOnEditCompetition] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [onDelete, setOnDelete] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const cancelAddCompetition = () => cancelAction(setOnAddCompetition);
  const cancelEditCompetition = () => {
    setOnEditCompetition(false);
    setEditingConfig(null);
  };
  const [rawData, setRawData] = useState([]);
  const [formattedData, setFormattedData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filterCompetition, setFilterCompetition] = useState(null);
  const [filterRoundType, setFilterRoundType] = useState(null);
  const [competitionFilterOptions, setCompetitionFilterOptions] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);

  const formatData = (data) => {
    const result = [];
    data?.map((el) => {
      const formattedObj = {};
      const nestedRows = [];
      formattedObj.id = el?.id;
      formattedObj.configuration_name =
        el?.configuration_name ?? el?.name ?? "-";
      formattedObj.competition_name =
        el?.competition_name ?? el?.round_name ?? "-";
      formattedObj.competition_id = el?.competition_id ?? el?.round_id;
      formattedObj.mode = el?.mode || "-";
      formattedObj.round_type =
        el?.round_type ?? (el?.is_team_round ? "Team" : "Individual");
      const awards = el?.awards ?? el?.templates ?? [];
      const configRoles = el?.roles ?? [];
      const rolesDisplay =
        Array.isArray(configRoles) && configRoles.length
          ? configRoles
              .map((r) => (typeof r === "object" ? r?.name ?? r?.label : r))
              .join(" - ")
          : "-";
      awards.forEach((item) => {
        const formattedNestedObj = {};
        formattedNestedObj.award = item?.award ?? "-";
        formattedNestedObj.file_name = item?.file_name ?? "-";
        formattedNestedObj.roles =
          Array.isArray(item?.roles) && item.roles.length
            ? item.roles
                .map((r) => (typeof r === "object" ? r?.name ?? r?.label : r))
                .join(" - ")
            : rolesDisplay;
        formattedNestedObj.show_disclaimer =
          item?.show_disclaimer ?? false ? "Yes" : "No";
        nestedRows.push(formattedNestedObj);
      });

      formattedObj.nestedData = nestedRows;
      result.push(formattedObj);
    });
    return result;
  };

  const listData = useCallback(async () => {
    setLoading(true);
    try {
      const filterData =
        await getConfiguredNewCompetitionsInPrintCertificates();
      const data = Array.isArray(filterData)
        ? filterData
        : filterData?.data ?? [];
      setRawData(data);
      setFormattedData(formatData(data));
    } finally {
      setLoading(false);
    }
  }, []);

  const filteredData = useMemo(() => {
    let list = formattedData;
    if (searchText?.trim()) {
      const q = searchText.trim().toLowerCase();
      list = list.filter(
        (row) =>
          (row.configuration_name &&
            String(row.configuration_name).toLowerCase().includes(q)) ||
          (row.competition_name &&
            String(row.competition_name).toLowerCase().includes(q))
      );
    }
    if (filterCompetition?.value) {
      list = list.filter(
        (row) =>
          row.competition_id === filterCompetition.value ||
          row.competition_name === filterCompetition.label
      );
    }
    if (
      filterRoundType !== null &&
      filterRoundType !== undefined &&
      filterRoundType !== ""
    ) {
      const roundLabel =
        filterRoundType === 0 || filterRoundType === "0"
          ? "Individual"
          : "Team";
      list = list.filter((row) =>
        String(row.round_type || "")
          .toLowerCase()
          .includes(roundLabel.toLowerCase())
      );
    }
    return list;
  }, [formattedData, searchText, filterCompetition, filterRoundType]);

  useEffect(() => {
    listData();
  }, [listData]);

  // Suppress ResizeObserver error - common issue with Ant Design tables
  useEffect(() => {
    const resizeObserverError = (e) => {
      if (
        e.message ===
          "ResizeObserver loop completed with undelivered notifications." ||
        e.message === "ResizeObserver loop limit exceeded"
      ) {
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener("error", resizeObserverError);
    return () => {
      window.removeEventListener("error", resizeObserverError);
    };
  }, []);

  const approveNewCompetitionFunction = (formData) => {
    setLoading(true);
    addNewCompetitionInPrintCertificates(formData)
      .then((res) => {
        showNotification(
          "success",
          res?.message || "Configuration added successfully",
          enqueueSnackbar,
          closeSnackbar
        );
        listData();
      })
      .catch((err) => {
        showNotification(
          "error",
          err?.message || err?.errors || "Failed to add configuration",
          enqueueSnackbar,
          closeSnackbar
        );
      })
      .finally(() => {
        setLoading(false);
        setOnAddCompetition(false);
      });
  };

  const approveEditCompetitionFunction = (formData) => {
    if (!editingConfig?.id) return;
    setLoading(true);
    updateCertificateConfiguration(editingConfig.id, formData)
      .then((res) => {
        showNotification(
          "success",
          res?.message || "Configuration updated successfully",
          enqueueSnackbar,
          closeSnackbar
        );
        listData();
        cancelEditCompetition();
      })
      .catch((err) => {
        showNotification(
          "error",
          err?.message || err?.errors || "Failed to update configuration",
          enqueueSnackbar,
          closeSnackbar
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDeleteConfig = async (id) => {
    setLoading(true);
    deleteCertificateConfiguration(id)
      .then(() => {
        showNotification(
          "success",
          "Configuration deleted successfully",
          enqueueSnackbar,
          closeSnackbar
        );
        listData();
        setOnDelete(false);
        setSelectedConfigId(null);
      })
      .catch((err) => {
        showNotification(
          "error",
          err?.message || "Failed to delete configuration",
          enqueueSnackbar,
          closeSnackbar
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const loadCompetitionOptions = async () => {
      setLoadingFilters(true);
      try {
        const data = await getConfigureNewCompetitionInPrintCertificatesFilter(
          "competition",
          { is_team_round: 0 }
        );
        setCompetitionFilterOptions(
          (Array.isArray(data) ? data : []).map((item) => ({
            label: item.filter_name ?? item.name,
            value: item.filter_id ?? item.id,
          }))
        );
      } finally {
        setLoadingFilters(false);
      }
    };
    loadCompetitionOptions();
  }, []);

  const parentColumns = [
    {
      title: "Configuration Name",
      dataIndex: "configuration_name",
      key: "configuration_name",
    },
    {
      title: "Competition Name",
      dataIndex: "competition_name",
      key: "competition_name",
    },
    { title: "Round Type", dataIndex: "round_type", key: "round_type" },
    { title: "Mode", dataIndex: "mode", key: "mode" },
    ...(isAdmin()
      ? [
          {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_, record) => (
              <Box display="flex" gap={1} alignItems="center">
                <EditIcon
                  style={{ cursor: "pointer", color: "#144A94" }}
                  onClick={() => {
                    const raw =
                      rawData.find((r) => r.id === record.id) || record;
                    setEditingConfig(raw);
                    setOnEditCompetition(true);
                  }}
                />
                <DeleteIcon
                  style={{ cursor: "pointer", color: "#E83042" }}
                  onClick={() => {
                    setSelectedConfigId(record.id);
                    setOnDelete(true);
                  }}
                />
              </Box>
            ),
          },
        ]
      : []),
  ];

  const nestedColumns = [
    { title: "Award", dataIndex: "award", key: "award" },
    { title: "File Name", dataIndex: "file_name", key: "file_name" },
    { title: "Roles", dataIndex: "roles", key: "roles" },
    {
      title: "Show Disclaimer",
      dataIndex: "show_disclaimer",
      key: "show_disclaimer",
    },
  ];

  const expandedRowRender = (record) => (
    <Table
      columns={nestedColumns}
      dataSource={record?.nestedData}
      pagination={false}
      rowKey={(row, index) => index}
    />
  );

  return (
    <Box className="wrapperBox">
      <LoadingBackdrop loading={loading} />
      <Grid
        className="firstRowContainer"
        container
        justifyContent="space-between"
      >
        <div className="dashboardAndSelfBtnDiv">
          <ReusableButton
            text="Dashboard"
            fontSize={14}
            bgColor="#F16774"
            height={36}
            width={125}
            br={18}
            to="/dashboard"
            iconType="home"
          />
          <ChevronRightIcon />
          <ReusableButton
            text="Configure Certificate"
            fontSize={14}
            bgColor="#F16774"
            height={36}
            br={18}
            onClick={() => {}}
          />
        </div>
      </Grid>
      <Grid className="tableContainer" container flexDirection={"column"}>
        <Grid
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <NunitoText
            value="CONFIGURED NEW COMPETITIONS"
            fontSize={40}
            align={"left"}
            fontWeight={700}
            marginBottom={40}
            italic
            color="#144A94"
          />
          <div>
            {isAdmin() && (
              <ReusableButton
                marginBottom={40}
                text="Add New Competition"
                fontSize={15}
                bgColor="#5E75C3"
                height={46}
                marginRight={20}
                iconType="add"
                onClick={() => setOnAddCompetition(true)}
              />
            )}
          </div>
        </Grid>
        <div
          style={{
            width: "100%",
            marginBottom: "20px",
            position: "relative",
          }}
        >
          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            <Grid item>
              <ReusableTextField
                type="search"
                width={320}
                height={54}
                bgColor="#F2F2F2"
                placeholder="Search by configuration or competition name"
                state={searchText}
                setState={setSearchText}
                onBlur
              />
            </Grid>
            <Grid item>
              <ReusableSelect
                type="filter"
                label="Competition"
                width={220}
                marginRight={0}
                bgColor="#FFF"
                borderColor="#E0E0E0"
                state={filterCompetition?.value ?? ""}
                setState={(value) =>
                  setFilterCompetition(
                    competitionFilterOptions.find(
                      (o) =>
                        o.value == value || String(o.value) === String(value)
                    ) ?? null
                  )
                }
                options={competitionFilterOptions.map((o) => ({
                  value: o.value,
                  option: o.label,
                }))}
                loading={loadingFilters}
              />
            </Grid>
            <Grid item>
              <ReusableSelect
                type="filter"
                label="Round Type"
                width={200}
                marginRight={0}
                bgColor="#FFF"
                borderColor="#E0E0E0"
                state={filterRoundType ?? ""}
                setState={(value) =>
                  setFilterRoundType(value === "" ? null : Number(value))
                }
                options={[
                  { value: "", option: "All" },
                  { value: 0, option: "Individual Round" },
                  { value: 1, option: "Team Round" },
                ]}
              />
            </Grid>
          </Grid>
          <Table
            rowKey={(row, index) => row.id ?? index}
            columns={parentColumns}
            dataSource={filteredData}
            expandable={{ expandedRowRender, defaultExpandedRowKeys: ["0"] }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} items`,
            }}
            scroll={{ x: "max-content" }}
          />
        </div>
        {/* Add New Competition Modal */}
        {isAdmin() && (
          <Modal open={onAddCompetition} onClose={() => cancelAddCompetition()}>
            <Box component="div" sx={{ outline: "none" }} tabIndex={-1}>
              <AddNewCompetitionForCertificateModal
                approveFunction={approveNewCompetitionFunction}
                cancelApprove={cancelAddCompetition}
              />
            </Box>
          </Modal>
        )}
        {/* Edit Competition Configuration Modal */}
        {isAdmin() && (
          <Modal
            open={onEditCompetition}
            onClose={() => cancelEditCompetition()}
          >
            <Box component="div" sx={{ outline: "none" }} tabIndex={-1}>
              <AddNewCompetitionForCertificateModal
                editMode
                configId={editingConfig?.id}
                approveFunction={approveEditCompetitionFunction}
                cancelApprove={cancelEditCompetition}
              />
            </Box>
          </Modal>
        )}
      </Grid>
      <Modal open={onDelete} onClose={() => setOnDelete(false)}>
        <ConfirmDeleteModal
          cancelDelete={() => setOnDelete(false)}
          deleteFunction={handleDeleteConfig}
          id={selectedConfigId}
          objectName="this configuration"
        />
      </Modal>
    </Box>
  );
};

export default ConfigureCertificate;
